import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, Camera, ImageIcon, Images, Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PACKING_STATUS_META } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import ImageLightbox from "@/components/ImageLightbox";

const CAN_EDIT = ["owner", "receiving"];
const JOB_EVENT_LABELS = {
  job_created: "Packing job created",
  job_saved: "Progress saved",
  packed: "Marked as packed",
  reopened: "Reopened by Owner",
  evidence_added: "Evidence photo added",
  evidence_removed: "Evidence photo removed",
};

export default function PackingJob() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [qtys, setQtys] = useState({});
  const [note, setNote] = useState("");
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [evZoom, setEvZoom] = useState(null);
  const [reopening, setReopening] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  const galRef = useRef(null);

  const canEdit = CAN_EDIT.includes(user.role);
  const editable = job?.status === "packing" && canEdit;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/packing/jobs/${jobId}`);
      setJob(data);
      setQtys(Object.fromEntries(data.items.map((i) => [i.order_item_id, i.qty_packed])));
      setNote(data.note || "");
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!job) {
    return (
      <div className="grid place-items-center py-24" data-testid="packing-job-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const meta = PACKING_STATUS_META[job.status] || PACKING_STATUS_META.packing;
  const totalReq = job.items.reduce((s, i) => s + i.qty_required, 0);
  const totalPacked = Object.values(qtys).reduce((s, q) => s + q, 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/packing/jobs/${jobId}`, {
        items: Object.entries(qtys).map(([order_item_id, q]) => ({ order_item_id, qty_packed: q })),
        note,
      });
      toast.success("Progress saved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!confirmSubmit) {
      setConfirmSubmit(true);
      return;
    }
    setBusy(true);
    try {
      await api.put(`/packing/jobs/${jobId}`, {
        items: Object.entries(qtys).map(([order_item_id, q]) => ({ order_item_id, qty_packed: q })),
        note,
      });
      await api.post(`/packing/jobs/${jobId}/submit`);
      toast.success("Packing complete — job locked");
      setConfirmSubmit(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    if (!reopenReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/packing/jobs/${jobId}/reopen`, { reason: reopenReason });
      toast.success("Job reopened");
      setReopening(false);
      setReopenReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const compressImage = async (file) => {
    try {
      const bmp = await createImageBitmap(file);
      const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
      if (scale >= 1 && file.size <= 3 * 1024 * 1024) return file;
      const w = Math.max(1, Math.round(bmp.width * scale));
      const h = Math.max(1, Math.round(bmp.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob || blob.size >= file.size) return file;
      return new File([blob], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
    } catch {
      return file; // graceful fallback — a camera/codec limitation must never break packing
    }
  };

  const uploadEvidence = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      await api.post(`/packing/jobs/${jobId}/evidence`, fd);
      toast.success("Evidence photo added");
      load();
    } catch (e) {
      toast.error(apiError(e, "Upload failed — check the photo and try again"));
    } finally {
      setUploading(false);
      if (camRef.current) camRef.current.value = "";
      if (galRef.current) galRef.current.value = "";
    }
  };

  const removeEvidence = async (evidenceId) => {
    setBusy(true);
    try {
      await api.delete(`/packing/jobs/${jobId}/evidence/${evidenceId}`);
      toast.success("Photo removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="packing-job-page">
      <button
        data-testid="back-to-packing"
        onClick={() => navigate("/packing")}
        className="mb-3 flex h-10 items-center gap-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Packing
      </button>

      <div className="mb-4 rounded-md border bg-card p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-extrabold tracking-tight" data-testid="packing-customer-name">{job.customer_name}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {job.batch_name} · #{job.id.slice(0, 8)} · by {job.created_by_name}
            </p>
          </div>
          <span data-testid="packing-status-badge" className={`shrink-0 rounded-md border px-2 py-0.5 text-xs font-bold ${meta.badge}`}>{meta.label}</span>
        </div>
        <div className="mt-3" data-testid="packing-progress-bar">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Packed</span>
            <span className="font-heading text-lg font-extrabold" data-testid="packing-progress-text">
              {totalPacked}<span className="text-sm text-muted-foreground">/{totalReq}</span>
            </span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-stone-100">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all"
              style={{ width: `${totalReq ? Math.min(100, (totalPacked / totalReq) * 100) : 0}%` }}
            />
          </div>
        </div>
        {job.status === "packed" && (
          <p className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800" data-testid="packed-banner">
            Packed by {job.packed_by_name} · {format(new Date(job.packed_at), "d MMM yyyy, HH:mm")} — job is locked
          </p>
        )}
        {job.pending_ready_items?.length > 0 && (
          <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="pending-ready-banner">
            {job.pending_ready_items.length} READY item{job.pending_ready_items.length === 1 ? "" : "s"} checked after this job was locked ({job.pending_ready_items.map((p) => `${p.product_name} × ${p.qty_required}`).join(", ")}). Owner: reopen the job to add {job.pending_ready_items.length === 1 ? "it" : "them"}.
          </p>
        )}
      </div>

      <h2 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Items to Pack</h2>
      <div className="space-y-2" data-testid="packing-items">
        {job.items.map((it) => (
          <div key={it.order_item_id} data-testid={`packing-item-${it.order_item_id}`} className="rounded-md border bg-card p-3">
            <div className="flex items-center gap-3">
              {productImage(it) ? (
                <img src={productImage(it)} alt={it.product_name} className="h-12 w-12 shrink-0 rounded-md border bg-stone-100 object-cover" />
              ) : (
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border bg-stone-100">
                  <ImageIcon className="h-5 w-5 text-stone-300" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="min-w-0 truncate text-sm font-bold">{it.product_name}</p>
                  {it.variant && <span className="shrink-0 rounded-md border bg-accent px-1.5 py-0.5 text-[10px] font-bold">{it.variant}</span>}
                </div>
                <p className="text-xs text-muted-foreground">Required: {it.qty_required} ({it.fulfillment_type === "READY" ? "READY · checked" : "FIFO allocated"})</p>
              </div>
              {editable ? (
                <div className="flex items-center gap-1.5 rounded-md border bg-white p-1">
                  <button
                    type="button"
                    data-testid={`pack-qty-minus-${it.order_item_id}`}
                    aria-label="Decrease"
                    className="flex h-9 w-9 items-center justify-center rounded-md bg-accent"
                    onClick={() => setQtys({ ...qtys, [it.order_item_id]: Math.max(0, (qtys[it.order_item_id] || 0) - 1) })}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span data-testid={`pack-qty-${it.order_item_id}`} className="w-8 text-center font-heading text-base font-extrabold">
                    {qtys[it.order_item_id] || 0}
                  </span>
                  <button
                    type="button"
                    data-testid={`pack-qty-plus-${it.order_item_id}`}
                    aria-label="Increase"
                    disabled={(qtys[it.order_item_id] || 0) >= it.qty_required}
                    className="flex h-9 w-9 items-center justify-center rounded-md bg-accent disabled:opacity-40"
                    onClick={() => setQtys({ ...qtys, [it.order_item_id]: Math.min(it.qty_required, (qtys[it.order_item_id] || 0) + 1) })}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <span className="shrink-0 font-heading text-lg font-extrabold">
                  {it.qty_packed}<span className="text-sm text-muted-foreground">/{it.qty_required}</span>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-md border bg-card p-4" data-testid="packing-evidence-section">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Packing Evidence</h2>
          {editable && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" data-testid="packing-take-photo" className="h-9" onClick={() => camRef.current?.click()} disabled={busy || uploading}>
                <Camera className="h-4 w-4" /> {uploading ? "Uploading…" : "Take Photo"}
              </Button>
              <Button variant="outline" size="sm" data-testid="packing-gallery" className="h-9" onClick={() => galRef.current?.click()} disabled={busy || uploading}>
                <Images className="h-4 w-4" /> {uploading ? "Uploading…" : "Gallery"}
              </Button>
            </div>
          )}
        </div>
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="packing-camera-input" onChange={(e) => uploadEvidence(e.target.files?.[0])} />
        <input ref={galRef} type="file" accept="image/*" className="hidden" data-testid="packing-gallery-input" onChange={(e) => uploadEvidence(e.target.files?.[0])} />
        {job.evidence?.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="packing-no-evidence">No photos yet — evidence is optional</p>
        ) : (
          <div className="grid grid-cols-3 gap-2" data-testid="packing-evidence-grid">
            {job.evidence.map((ev) => (
              <div key={ev.id} className="group relative">
                <button
                  type="button"
                  data-testid={`evidence-view-${ev.id}`}
                  aria-label="View evidence photo"
                  onClick={() => setEvZoom({ src: fileUrl(ev.path), alt: ev.original_filename || "evidence" })}
                  className="block w-full"
                >
                  <img src={fileUrl(ev.path)} alt={ev.original_filename || "evidence"} className="aspect-square w-full rounded-md border object-cover" />
                </button>
                {editable && (
                  <button
                    type="button"
                    data-testid={`evidence-delete-${ev.id}`}
                    aria-label="Remove photo"
                    onClick={() => removeEvidence(ev.id)}
                    className="absolute right-1 top-1 rounded-md bg-black/60 p-1.5 text-white"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 rounded-md border bg-card p-4">
        <h2 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Note</h2>
        {editable ? (
          <Textarea data-testid="packing-note" placeholder="Optional packing note" value={note} onChange={(e) => setNote(e.target.value)} />
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="packing-note-readonly">{job.note || "—"}</p>
        )}
      </div>

      {editable && (
        <div className="sticky bottom-20 z-30 mt-4 space-y-2 rounded-md border bg-white/95 p-3 backdrop-blur md:bottom-4" data-testid="packing-action-bar">
          <Button variant="outline" data-testid="packing-save" className="h-11 w-full" onClick={save} disabled={busy}>
            Save Progress
          </Button>
          <Button
            variant={confirmSubmit ? "default" : "default"}
            data-testid="packing-submit"
            className={`h-11 w-full ${confirmSubmit ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
            onClick={submit}
            disabled={busy || totalPacked === 0}
          >
            {confirmSubmit ? "Confirm — mark as PACKED (locks job)" : "Mark as Packed"}
          </Button>
        </div>
      )}

      {job.status === "packed" && user.role === "owner" && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="packing-reopen-section">
          {!reopening ? (
            <Button variant="outline" data-testid="packing-reopen-button" className="h-11 w-full" onClick={() => setReopening(true)}>
              Owner: Reopen this job
            </Button>
          ) : (
            <div className="space-y-2">
              <Input
                data-testid="packing-reopen-reason"
                className="h-11 bg-white"
                placeholder="Reason for reopening (required)"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
              />
              <div className="flex gap-2">
                <Button variant="outline" className="h-11 flex-1" onClick={() => setReopening(false)} data-testid="packing-reopen-cancel">
                  Cancel
                </Button>
                <Button data-testid="packing-reopen-confirm" className="h-11 flex-1" onClick={reopen} disabled={busy || !reopenReason.trim()}>
                  Confirm Reopen
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {job.audit?.length > 0 && (
        <div className="mt-5" data-testid="packing-audit">
          <h2 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Job History</h2>
          <div className="space-y-1.5">
            {[...job.audit].reverse().map((e, i) => (
              <div key={i} className="rounded-md border bg-card p-3">
                <p className="text-xs font-semibold">{JOB_EVENT_LABELS[e.event] || e.event}</p>
                <p className="text-[11px] text-muted-foreground">
                  {e.by_name} · {format(new Date(e.at), "d MMM yyyy, HH:mm")}
                  {e.details?.reason && ` · ${e.details.reason}`}
                  {e.details?.packed_total != null && ` · ${e.details.packed_total} units`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      {evZoom && <ImageLightbox src={evZoom.src} alt={evZoom.alt} onClose={() => setEvZoom(null)} />}
      <div className="h-6" />
    </div>
  );
}
